import React, { useState, useEffect } from "react";
import {
  Modal,
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Space,
  Button,
  Divider,
  Descriptions,
  Timeline,
  Input,
  Select,
  message,
  Spin,
  Tooltip,
} from "antd";
import {
  PhoneOutlined,
  ClockCircleOutlined,
  UserOutlined,
  CalendarOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  HistoryOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  EditOutlined,
  MessageOutlined,
  GiftOutlined,
} from "@ant-design/icons";
import {
  updateCallRequestStatus,
  assignCallRequest,
  getCallRequestHistory,
} from "../services/callRequestApiService";
import { getUsers } from "../../users/services/userService";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const CallRequestDetailModal = ({
  visible,
  callRequest,
  onClose,
  onUpdate,
}) => {
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedAgent, setSelectedAgent] = useState("");

  // New state for events history
  const [eventsHistory, setEventsHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // New state for agents
  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [agentsError, setAgentsError] = useState(null);

  const getStatusColor = (status) => {
    const colors = {
      requested: "blue",
      assigned: "orange",
      calling: "purple",
      completed: "green",
      no_answer: "red",
      reschedule: "geekblue",
    };
    return colors[status] || "default";
  };

  const getPreferredTimeText = (preferredTime) => {
    const timeMap = {
      now: "Immediately",
      "30min": "Within 30 minutes",
      later_today: "Later today",
      tomorrow: "Tomorrow",
      specific: "Specific scheduled time",
    };
    return timeMap[preferredTime] || preferredTime;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not specified";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return "Not specified";
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const handleStatusUpdate = async () => {
    if (!selectedStatus) return;

    setLoading(true);
    try {
      const result = await updateCallRequestStatus(
        callRequest.id,
        selectedStatus,
        notes
      );

      if (result.success) {
        message.success(`Status updated to ${selectedStatus}`);
        setSelectedStatus("");
        setNotes("");
        onUpdate?.();
        // Refresh events history after status update
        fetchEventsHistory();
      } else {
        message.error("Failed to update status");
      }
    } catch (error) {
      message.error("Failed to update status");
    } finally {
      setLoading(false);
    }
  };

  const handleAssignAgent = async () => {
    if (!selectedAgent) return;

    setLoading(true);
    try {
      const result = await assignCallRequest(callRequest.id, selectedAgent);

      if (result.success) {
        message.success("Call request assigned successfully");
        setSelectedAgent("");
        onUpdate?.();
        // Refresh events history after assignment
        fetchEventsHistory();
      } else {
        message.error("Failed to assign call request");
      }
    } catch (error) {
      message.error("Failed to assign call request");
    } finally {
      setLoading(false);
    }
  };

  const handleCallNow = () => {
    if (callRequest.phone) {
      window.open(`tel:${callRequest.phone}`, "_blank");
    } else {
      message.error("No phone number available");
    }
  };

  // Fetch call request events history
  const fetchEventsHistory = async () => {
    if (!callRequest?.id) return;

    setLoadingHistory(true);
    try {
      const result = await getCallRequestHistory(callRequest.id);
      if (result.success) {
        setEventsHistory(result.data || []);
      } else {
        console.error("Failed to fetch events history:", result.error);
        setEventsHistory([]);
      }
    } catch (error) {
      console.error("Error fetching events history:", error);
      setEventsHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Load agents with call management permissions
  const fetchAgents = async () => {
    setLoadingAgents(true);
    setAgentsError(null);
    try {
      const result = await getUsers({
        role: "agent",
        status: "active",
        limit: 100, // Get more agents if needed
      });

      if (result.success && result.users) {
        // Filter agents with call management permissions
        const callAgents = result.users.filter(
          (user) => user.permissions && user.permissions.includes("call:manage")
        );
        setAgents(callAgents);
      } else if (result && result.users) {
        // Alternative response format
        const callAgents = result.users.filter(
          (user) => user.permissions && user.permissions.includes("call:manage")
        );
        setAgents(callAgents);
      } else {
        const errorMsg = result.error || "Failed to fetch agents";
        console.error("Failed to fetch agents:", errorMsg);
        setAgentsError(errorMsg);
        setAgents([]);
      }
    } catch (error) {
      const errorMsg = error.message || "Error fetching agents";
      console.error("Error fetching agents:", errorMsg);
      setAgentsError(errorMsg);
      setAgents([]);
    } finally {
      setLoadingAgents(false);
    }
  };

  // Load agents when modal opens
  useEffect(() => {
    if (visible) {
      fetchAgents();
    }
  }, [visible]);

  // Load events history when modal opens
  useEffect(() => {
    if (visible && callRequest?.id) {
      fetchEventsHistory();
    }
  }, [visible, callRequest?.id]);

  // Business hours configuration
  const businessHoursConfig = {
    start: 9, // 9 AM
    end: 17, // 5 PM
    days: [1, 2, 3, 4, 5], // Monday = 1, Sunday = 0
    timezone: "local", // Could be configurable for different regions
  };

  // Helper function to calculate working hours SLA
  const calculateWorkingHoursSLA = (slaDueAt) => {
    if (!slaDueAt) return { status: "no_sla", message: "No SLA defined" };

    const now = new Date();
    const dueDate = new Date(slaDueAt);

    // Check if current time is within business hours
    const isBusinessHours = () => {
      const day = now.getDay();
      const hour = now.getHours();
      return (
        businessHoursConfig.days.includes(day) &&
        hour >= businessHoursConfig.start &&
        hour < businessHoursConfig.end
      );
    };

    // Calculate working minutes until SLA due
    const calculateWorkingMinutes = () => {
      let workingMinutes = 0;
      let current = new Date(now);

      while (current < dueDate) {
        const day = current.getDay();
        const hour = current.getHours();

        // Only count business hours on business days
        if (businessHoursConfig.days.includes(day)) {
          if (hour < businessHoursConfig.start) {
            // Before business hours, count from start of business day
            workingMinutes +=
              (businessHoursConfig.end - businessHoursConfig.start) * 60;
          } else if (
            hour >= businessHoursConfig.start &&
            hour < businessHoursConfig.end
          ) {
            // During business hours
            workingMinutes += (businessHoursConfig.end - hour) * 60;
          }
          // After business hours, don't count
        }

        // Move to next day
        current.setDate(current.getDate() + 1);
        current.setHours(businessHoursConfig.start, 0, 0, 0);
      }

      return workingMinutes;
    };

    const workingMinutes = calculateWorkingMinutes();
    const isOverdue = workingMinutes <= 0;

    if (isOverdue) {
      return {
        status: "breached",
        message: "SLA Breached",
        color: "#ff4d4f",
        workingMinutes: 0,
        isBusinessHours: isBusinessHours(),
      };
    }

    // Convert to hours and minutes for display
    const hours = Math.floor(workingMinutes / 60);
    const minutes = workingMinutes % 60;

    let status, color;
    if (workingMinutes <= 60) {
      // 1 hour or less
      status = "critical";
      color = "#ff4d4f";
    } else if (workingMinutes <= 240) {
      // 4 hours or less
      status = "warning";
      color = "#fa8c16";
    } else {
      status = "normal";
      color = "#52c41a";
    }

    return {
      status,
      message: `${hours}h ${minutes}m remaining`,
      color,
      workingMinutes,
      isBusinessHours: isBusinessHours(),
      displayText: hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`,
    };
  };

  // Helper function to get agent name by ID
  const getAgentName = (agentId) => {
    if (!agentId) return "Unknown";

    // If agentId is an object (populated user), extract the name
    if (typeof agentId === "object" && agentId !== null) {
      if (agentId.firstName && agentId.lastName) {
        return `${agentId.firstName} ${agentId.lastName}`;
      } else if (agentId.name) {
        return agentId.name;
      } else if (agentId.email) {
        return agentId.email;
      }
      return "Unknown Agent";
    }

    // If agentId is a string (ID), find the agent in the agents list
    const agent = agents.find((a) => (a._id || a.id) === agentId);
    if (agent) {
      return agent.firstName && agent.lastName
        ? `${agent.firstName} ${agent.lastName}`
        : agent.name || agent.email;
    }
    return agentId; // Return ID if agent not found
  };

  // Helper function to get event icon
  const getEventIcon = (eventType) => {
    const iconMap = {
      "call.requested": <PhoneOutlined style={{ color: "#1890ff" }} />,
      "call.assigned": <UserOutlined style={{ color: "#52c41a" }} />,
      "call.started": <PhoneOutlined style={{ color: "#722ed1" }} />,
      "call.calling": <PhoneOutlined style={{ color: "#722ed1" }} />,
      "call.completed": <CheckCircleOutlined style={{ color: "#52c41a" }} />,
      "call.no_answer": <CloseCircleOutlined style={{ color: "#ff4d4f" }} />,
      "call.rescheduled": <CalendarOutlined style={{ color: "#fa8c16" }} />,
      "call.escalated": (
        <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />
      ),
      "call.reassigned": <UserOutlined style={{ color: "#1890ff" }} />,
      "call.status_updated": <FileTextOutlined style={{ color: "#1890ff" }} />,
    };
    return iconMap[eventType] || <HistoryOutlined />;
  };

  // Helper function to get event description
  const getEventDescription = (eventType) => {
    const descriptions = {
      "call.requested": "Call requested by customer",
      "call.assigned": `Assigned to agent`,
      "call.started": "Call started",
      "call.calling": "Call in progress",
      "call.completed": "Call completed successfully",
      "call.no_answer": "No answer from customer",
      "call.rescheduled": "Call rescheduled",
      "call.escalated": "Call escalated",
      "call.reassigned": "Call reassigned to different agent",
      "call.status_updated": "Status updated",
    };

    let description = descriptions[eventType] || "Event occurred";

    // Add actor information
    if (eventType === "call.assigned") {
      description += " by agent";
    } else if (eventType === "call.reassigned") {
      description += " by agent";
    } else if (eventType === "call.status_updated") {
      description += " by system";
    }

    return description;
  };

  // Helper function to get event color for Timeline
  const getEventColor = (eventType) => {
    if (eventType.includes("completed")) {
      return "green";
    }
    if (eventType.includes("no_answer") || eventType.includes("escalated")) {
      return "red";
    }
    if (eventType.includes("assigned") || eventType.includes("reassigned")) {
      return "blue";
    }
    return "default";
  };

  if (!callRequest) return null;

  return (
    <Modal
      title={
        <Space>
          <PhoneOutlined />
          Call Request Details
          <Tag color={getStatusColor(callRequest.status)}>
            {callRequest.status.toUpperCase()}
          </Tag>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      destroyOnHidden
    >
      <div style={{ padding: "16px 0" }}>
        {/* Call Timeline Summary */}
        <Card
          title={
            <Space>
              <ClockCircleOutlined style={{ color: "#1890ff" }} />
              Call Summary
            </Space>
          }
          size="small"
          style={{ marginBottom: 16, border: "2px solid #1890ff" }}
        >
          <Row gutter={16}>
            <Col span={24}>
              <div
                style={{
                  background: "#f0f8ff",
                  padding: "16px",
                  borderRadius: "8px",
                  border: "1px solid #91d5ff",
                }}
              >
                <div style={{ marginBottom: "16px", textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: "20px",
                      fontWeight: "bold",
                      color: "#1890ff",
                      marginBottom: "8px",
                    }}
                  >
                    📅{" "}
                    {callRequest.scheduledAt
                      ? formatDate(callRequest.scheduledAt)
                      : "Not Scheduled"}
                  </div>
                  {callRequest.scheduledAt && (
                    <div style={{ fontSize: "16px", color: "#666" }}>
                      ⏰ {formatTime(callRequest.scheduledAt)}
                    </div>
                  )}
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "12px",
                    background: "white",
                    borderRadius: "6px",
                    border: "1px solid #d9d9d9",
                  }}
                >
                  <div>
                    <Text strong>📞 Requested:</Text>
                    <div
                      style={{
                        marginTop: "4px",
                        fontSize: "12px",
                        color: "#666",
                      }}
                    >
                      {formatDate(callRequest.requestedAt)}
                    </div>
                  </div>

                  {callRequest.scheduledAt && (
                    <div style={{ textAlign: "center" }}>
                      <Text strong>⏱️ Status:</Text>
                      <div style={{ marginTop: "4px" }}>
                        {new Date() > new Date(callRequest.scheduledAt) ? (
                          <Tag color="red" icon={<ExclamationCircleOutlined />}>
                            CALL TIME PASSED
                          </Tag>
                        ) : (
                          <Tag color="green" icon={<CheckCircleOutlined />}>
                            SCHEDULED
                          </Tag>
                        )}
                      </div>
                    </div>
                  )}

                  <div style={{ textAlign: "right" }}>
                    <Text strong>🏢 Business Hours:</Text>
                    <div
                      style={{
                        marginTop: "4px",
                        fontSize: "12px",
                        color: "#666",
                      }}
                    >
                      {(() => {
                        const now = new Date();
                        const hour = now.getHours();
                        const isBusinessHours =
                          hour >= 9 &&
                          hour < 17 &&
                          now.getDay() >= 1 &&
                          now.getDay() <= 5;
                        return isBusinessHours ? "🟢 Open" : "🟠 Closed";
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Card>

        {/* Basic Information */}
        <Card
          title={
            <Space>
              <InfoCircleOutlined />
              Basic Information
              <Tooltip
                title="Core details about the call request including customer info, timing, and SLA status"
                placement="top"
              >
                <InfoCircleOutlined
                  style={{
                    color: "#1890ff",
                    cursor: "help",
                    fontSize: "16px",
                    marginLeft: "8px",
                  }}
                />
              </Tooltip>
            </Space>
          }
          size="small"
          style={{ marginBottom: 16 }}
        >
          {/* Overdue Alert Banner */}
          {(() => {
            // Use backend SLA info if available, otherwise calculate locally
            const slaInfo =
              callRequest.slaInfo ||
              calculateWorkingHoursSLA(callRequest.scheduledAt);
            if (slaInfo.status === "breached") {
              const overdueTime = Math.abs(slaInfo.workingMinutes);
              const overdueHours = Math.floor(overdueTime / 60);
              const overdueMinutes = overdueTime % 60;
              const overdueText =
                overdueHours > 0
                  ? `${overdueHours}h ${overdueMinutes}m overdue`
                  : `${overdueMinutes}m overdue`;

              return (
                <div
                  style={{
                    padding: "16px",
                    backgroundColor: "#fff2f0",
                    border: "2px solid #ff4d4f",
                    borderRadius: "8px",
                    marginBottom: "16px",
                    textAlign: "center",
                  }}
                >
                  <Space direction="vertical" size="small">
                    <Text strong style={{ color: "#ff4d4f", fontSize: "16px" }}>
                      🚨 URGENT - SLA BREACHED
                    </Text>
                    <Text style={{ color: "#ff4d4f", fontSize: "14px" }}>
                      {overdueText} • Call was scheduled for{" "}
                      {formatDate(callRequest.scheduledAt)}
                    </Text>
                    <Space>
                      <Tag color="red" icon={<ExclamationCircleOutlined />}>
                        Immediate Action Required
                      </Tag>
                      <Tag color="orange" icon={<ClockCircleOutlined />}>
                        {slaInfo.isBusinessHours
                          ? "Within Business Hours"
                          : "Outside Business Hours"}
                      </Tag>
                    </Space>
                  </Space>
                </div>
              );
            }
            return null;
          })()}

          <Row gutter={16}>
            <Col span={12}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Customer ID">
                  <Text code>{callRequest.customerId}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Phone">
                  <Text>{callRequest.phone}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag color={getStatusColor(callRequest.status)}>
                    {callRequest.status.toUpperCase()}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <Tooltip title="When the customer requested the call">
                      Requested At
                    </Tooltip>
                  }
                >
                  <Text>{formatDate(callRequest.requestedAt)}</Text>
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <Tooltip title="When this call request was created in the system">
                      Created At
                    </Tooltip>
                  }
                >
                  <Text>{formatDate(callRequest.createdAt)}</Text>
                </Descriptions.Item>
              </Descriptions>
            </Col>
            <Col span={12}>
              <Descriptions column={1} size="small">
                <Descriptions.Item
                  label={
                    <Tooltip title="When the customer wants the call to happen">
                      Scheduled Call Date/Time
                    </Tooltip>
                  }
                >
                  <Text>
                    {callRequest.scheduledAt ? (
                      <Space direction="vertical" size="small">
                        <div style={{ fontWeight: "bold", fontSize: "16px" }}>
                          {formatDate(callRequest.scheduledAt)}
                        </div>
                        <div style={{ fontSize: "14px", color: "#1890ff" }}>
                          ⏰ {formatTime(callRequest.scheduledAt)}
                        </div>
                      </Space>
                    ) : (
                      "Not scheduled yet"
                    )}
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <Tooltip title="Customer's time preference for the call">
                      Time Preference
                    </Tooltip>
                  }
                >
                  <Text>
                    {callRequest.preferredTime === "specific" &&
                    callRequest.scheduledAt ? (
                      <span style={{ color: "#52c41a", fontWeight: "500" }}>
                        ✅ Specific time scheduled
                      </span>
                    ) : (
                      getPreferredTimeText(callRequest.preferredTime)
                    )}
                  </Text>
                </Descriptions.Item>

                <Descriptions.Item label="Call Status">
                  {(() => {
                    // Use backend SLA info if available, otherwise calculate locally
                    const slaInfo =
                      callRequest.slaInfo ||
                      calculateWorkingHoursSLA(callRequest.scheduledAt);
                    return (
                      <div>
                        <Tag
                          color={slaInfo.color}
                          icon={<ClockCircleOutlined />}
                          style={{ fontWeight: "bold" }}
                        >
                          {slaInfo.status === "breached"
                            ? "OVERDUE"
                            : slaInfo.status === "critical"
                              ? "URGENT"
                              : slaInfo.status === "warning"
                                ? "WARNING"
                                : slaInfo.status === "normal"
                                  ? "ON TRACK"
                                  : "NO SCHEDULE"}
                        </Tag>
                        {slaInfo.status !== "no_sla" && (
                          <div
                            style={{
                              marginTop: "8px",
                              fontSize: "12px",
                              color: "#666",
                            }}
                          >
                            {slaInfo.status === "breached"
                              ? "Call time has passed"
                              : `${slaInfo.displayText} until call`}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>
        </Card>

        {/* Metadata Information */}
        {callRequest.metadata && (
          <Card
            title={
              <Space>
                <InfoCircleOutlined />
                Additional Information
                <Tooltip
                  title="Metadata and source information for this call request"
                  placement="top"
                >
                  <InfoCircleOutlined
                    style={{
                      color: "#1890ff",
                      cursor: "help",
                      fontSize: "16px",
                      marginLeft: "8px",
                    }}
                  />
                </Tooltip>
              </Space>
            }
            size="small"
            style={{ marginBottom: 16 }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Source">
                    <Tag color="blue">
                      {callRequest.metadata.source || "Unknown"}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Route">
                    <Text>{callRequest.metadata.route || "Not specified"}</Text>
                  </Descriptions.Item>
                  {callRequest.metadata.bookingId && (
                    <Descriptions.Item label="Original Booking ID">
                      <Text code>{callRequest.metadata.bookingId}</Text>
                    </Descriptions.Item>
                  )}
                  {callRequest.metadata.originalQuoteId && (
                    <Descriptions.Item label="Original Quote ID">
                      <Text code>{callRequest.metadata.originalQuoteId}</Text>
                    </Descriptions.Item>
                  )}
                  {callRequest.metadata.originalPreferredTime && (
                    <Descriptions.Item label="Original Preferred Time">
                      <Text code>
                        {callRequest.metadata.originalPreferredTime}
                      </Text>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Col>
              <Col span={12}>
                <Descriptions column={1} size="small">
                  {callRequest.metadata.quoteAmount && (
                    <Descriptions.Item label="Quote Amount">
                      <Text strong>R {callRequest.metadata.quoteAmount}</Text>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Col>
            </Row>
          </Card>
        )}

        {/* Assignment Information */}
        <Card
          title={
            <Space>
              <UserOutlined />
              Assignment
              {loadingAgents && <Spin size="small" />}
              <Tooltip
                title="Manage agent assignment, view current assignment details, and track assignment history"
                placement="top"
              >
                <InfoCircleOutlined
                  style={{
                    color: "#1890ff",
                    cursor: "help",
                    fontSize: "16px",
                    marginLeft: "8px",
                  }}
                />
              </Tooltip>
            </Space>
          }
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Current Assignment">
                  {callRequest.assignedToUserId ? (
                    <Space>
                      <UserOutlined />
                      <Text>{getAgentName(callRequest.assignedToUserId)}</Text>
                      <Tag color="green">Assigned</Tag>
                    </Space>
                  ) : (
                    <Tag color="orange">Unassigned</Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Quote ID">
                  <Text code>{callRequest.quoteId}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Assignment Date">
                  {callRequest.assignedToUserId ? (
                    <Text>{formatDate(callRequest.updatedAt)}</Text>
                  ) : (
                    <Text type="secondary">Not assigned yet</Text>
                  )}
                </Descriptions.Item>
              </Descriptions>
            </Col>
            <Col span={12}>
              {/* Assignment Actions */}
              <Space direction="vertical" style={{ width: "100%" }}>
                <Text strong>Assign to Agent:</Text>
                <Space>
                  <Select
                    placeholder={
                      loadingAgents
                        ? "Loading agents..."
                        : agents.length === 0
                          ? "No agents available"
                          : "Select agent"
                    }
                    value={selectedAgent}
                    onChange={setSelectedAgent}
                    style={{ width: "200px" }}
                    loading={loadingAgents}
                    showSearch
                    disabled={loadingAgents || agents.length === 0}
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {agents.map((agent) => (
                      <Option
                        key={agent._id || agent.id}
                        value={agent._id || agent.id}
                      >
                        {agent.firstName && agent.lastName
                          ? `${agent.firstName} ${agent.lastName}`
                          : agent.name || agent.email}{" "}
                        ({agent.email})
                      </Option>
                    ))}
                  </Select>
                  <Button
                    size="small"
                    icon={<ReloadOutlined />}
                    onClick={fetchAgents}
                    loading={loadingAgents}
                  >
                    Refresh
                  </Button>
                </Space>
                <Button
                  type="primary"
                  size="small"
                  onClick={handleAssignAgent}
                  loading={loading}
                  disabled={!selectedAgent || agents.length === 0}
                >
                  Assign
                </Button>
                {agentsError && (
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <Text type="danger" style={{ fontSize: "12px" }}>
                      Error loading agents: {agentsError}
                    </Text>
                    <Button
                      size="small"
                      onClick={fetchAgents}
                      icon={<ReloadOutlined />}
                    >
                      Retry
                    </Button>
                  </Space>
                )}
                {agents.length === 0 && !loadingAgents && !agentsError && (
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    No agents with call management permissions found
                  </Text>
                )}
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Call Scheduling */}
        <Card
          title={
            <Space>
              <ClockCircleOutlined style={{ color: "#1890ff" }} />
              Call Scheduling
              <Tooltip
                title="Call scheduling details and timing information"
                placement="top"
              >
                <InfoCircleOutlined
                  style={{
                    color: "#1890ff",
                    cursor: "help",
                    fontSize: "16px",
                    marginLeft: "8px",
                  }}
                />
              </Tooltip>
            </Space>
          }
          size="small"
          style={{ marginBottom: 16 }}
        >
          {(() => {
            // Use backend SLA info if available, otherwise calculate locally
            const slaInfo =
              callRequest.slaInfo ||
              calculateWorkingHoursSLA(callRequest.scheduledAt);
            return (
              <Row gutter={16}>
                <Col span={12}>
                  <Descriptions column={1} size="small">
                    <Descriptions.Item
                      label={
                        <Tooltip title="Current call scheduling status">
                          Call Status
                        </Tooltip>
                      }
                    >
                      <Tag color={slaInfo.color} style={{ fontWeight: "bold" }}>
                        {slaInfo.message}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={
                        <Tooltip title="When the call is scheduled to happen">
                          Call Scheduled For
                        </Tooltip>
                      }
                    >
                      <Text strong>{formatDate(callRequest.scheduledAt)}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={
                        <Tooltip title="Our standard business hours">
                          Business Hours
                        </Tooltip>
                      }
                    >
                      <Text>
                        {businessHoursConfig.days.length === 5
                          ? "Monday - Friday"
                          : businessHoursConfig.days.length === 7
                            ? "Every Day"
                            : `${businessHoursConfig.days.length} days per week`}
                        , {businessHoursConfig.start}:00 -{" "}
                        {businessHoursConfig.end}:00
                      </Text>
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
                <Col span={12}>
                  <div
                    style={{
                      padding: "16px",
                      backgroundColor:
                        slaInfo.color === "#ff4d4f"
                          ? "#fff2f0"
                          : slaInfo.color === "#fa8c16"
                            ? "#fff7e6"
                            : "#f6ffed",
                      border: `2px solid ${slaInfo.color}`,
                      borderRadius: "8px",
                    }}
                  >
                    <Space
                      direction="vertical"
                      style={{ width: "100%" }}
                      size="middle"
                    >
                      <div style={{ textAlign: "center" }}>
                        <Text
                          strong
                          style={{ color: slaInfo.color, fontSize: "16px" }}
                        >
                          {slaInfo.status === "breached" &&
                            "🚨 URGENT - Call Time Passed"}
                          {slaInfo.status === "critical" &&
                            "⚠️ CRITICAL - Call within 1 hour"}
                          {slaInfo.status === "warning" &&
                            "⏰ WARNING - Call within 4 hours"}
                          {slaInfo.status === "normal" &&
                            "✅ ON TRACK - Plenty of time"}
                          {slaInfo.status === "no_sla" &&
                            "❓ NO CALL SCHEDULED"}
                        </Text>
                      </div>

                      {slaInfo.status !== "no_sla" && (
                        <>
                          <div
                            style={{
                              background: "white",
                              padding: "12px",
                              borderRadius: "6px",
                              border: "1px solid #d9d9d9",
                            }}
                          >
                            <Text strong>⏱️ Call Timing:</Text>
                            <div style={{ marginTop: "8px" }}>
                              <Text style={{ fontSize: "14px" }}>
                                {slaInfo.status === "breached" ? (
                                  <span style={{ color: "#ff4d4f" }}>
                                    <strong>PAST DUE:</strong>{" "}
                                    {slaInfo.displayText}
                                  </span>
                                ) : (
                                  <span style={{ color: slaInfo.color }}>
                                    <strong>Time Until Call:</strong>{" "}
                                    {slaInfo.displayText}
                                  </span>
                                )}
                              </Text>
                            </div>
                          </div>

                          <div
                            style={{
                              background: "white",
                              padding: "12px",
                              borderRadius: "6px",
                              border: "1px solid #d9d9d9",
                            }}
                          >
                            <Text strong>🏢 Business Hours:</Text>
                            <div style={{ marginTop: "8px" }}>
                              <Text style={{ fontSize: "14px" }}>
                                {slaInfo.isBusinessHours
                                  ? "🟢 Within Business Hours - Normal call scheduling"
                                  : "🟠 Outside Business Hours - Extended call scheduling"}
                              </Text>
                            </div>
                          </div>
                        </>
                      )}
                    </Space>
                  </div>
                </Col>
              </Row>
            );
          })()}
        </Card>

        {/* Overdue Recovery Actions - Only show when SLA is breached */}
        {(() => {
          // Use backend SLA info if available, otherwise calculate locally
          const slaInfo =
            callRequest.slaInfo ||
            calculateWorkingHoursSLA(callRequest.scheduledAt);
          if (slaInfo.status === "breached") {
            return (
              <Card
                title={
                  <Space>
                    <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />
                    Overdue Recovery Actions
                    <Tooltip
                      title="Immediate actions required to recover from SLA breach and prevent future occurrences"
                      placement="top"
                    >
                      <InfoCircleOutlined
                        style={{
                          color: "#1890ff",
                          cursor: "help",
                          fontSize: "16px",
                          marginLeft: "8px",
                        }}
                      />
                    </Tooltip>
                  </Space>
                }
                size="small"
                style={{ marginBottom: 16, borderColor: "#ff4d4f" }}
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <Descriptions column={1} size="small">
                      <Descriptions.Item
                        label={
                          <Tooltip title="Why this SLA was breached">
                            Breach Analysis
                          </Tooltip>
                        }
                      >
                        <Text type="secondary">
                          {callRequest.metadata?.breachReason ||
                            "Not yet analyzed"}
                        </Text>
                      </Descriptions.Item>
                      <Descriptions.Item
                        label={
                          <Tooltip title="Previous attempts to resolve this issue">
                            Resolution Attempts
                          </Tooltip>
                        }
                      >
                        <Text type="secondary">
                          {callRequest.metadata?.resolutionAttempts ||
                            "No previous attempts"}
                        </Text>
                      </Descriptions.Item>
                      <Descriptions.Item
                        label={
                          <Tooltip title="Impact level on customer satisfaction">
                            Customer Impact
                          </Tooltip>
                        }
                      >
                        <Tag color="red" style={{ fontWeight: "bold" }}>
                          🚨 HIGH - Response Overdue
                        </Tag>
                      </Descriptions.Item>
                    </Descriptions>
                  </Col>
                  <Col span={12}>
                    <div
                      style={{
                        background: "#fff2f0",
                        padding: "16px",
                        borderRadius: "8px",
                        border: "1px solid #ffccc7",
                      }}
                    >
                      <Text
                        strong
                        style={{ color: "#ff4d4f", fontSize: "16px" }}
                      >
                        🚨 IMMEDIATE ACTIONS REQUIRED:
                      </Text>

                      <div style={{ marginTop: "16px" }}>
                        <Space
                          direction="vertical"
                          size="middle"
                          style={{ width: "100%" }}
                        >
                          <div>
                            <Text strong style={{ color: "#ff4d4f" }}>
                              🔥 Priority 1:
                            </Text>
                            <Button
                              size="small"
                              type="primary"
                              danger
                              style={{ marginLeft: "8px" }}
                              icon={<ExclamationCircleOutlined />}
                            >
                              Escalate to Supervisor
                            </Button>
                          </div>

                          <div>
                            <Text strong style={{ color: "#fa8c16" }}>
                              ⚡ Priority 2:
                            </Text>
                            <Space wrap style={{ marginLeft: "8px" }}>
                              <Button
                                size="small"
                                type="default"
                                icon={<MessageOutlined />}
                              >
                                Send Apology Message
                              </Button>
                              <Button
                                size="small"
                                type="default"
                                icon={<GiftOutlined />}
                              >
                                Offer Compensation
                              </Button>
                            </Space>
                          </div>

                          <div>
                            <Text strong style={{ color: "#1890ff" }}>
                              📅 Priority 3:
                            </Text>
                            <Button
                              size="small"
                              type="default"
                              icon={<CalendarOutlined />}
                              style={{ marginLeft: "8px" }}
                            >
                              Set New Deadline
                            </Button>
                          </div>
                        </Space>
                      </div>

                      <div
                        style={{
                          marginTop: "16px",
                          padding: "12px",
                          background: "white",
                          borderRadius: "6px",
                          border: "1px solid #d9d9d9",
                        }}
                      >
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          💡 <strong>Why these actions matter:</strong> They
                          help recover customer satisfaction, prevent future
                          breaches, and maintain our service quality standards.
                        </Text>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card>
            );
          }
          return null;
        })()}

        {/* Assignment History */}
        <Card
          title={
            <Space>
              <UserOutlined />
              Assignment History
              <Tooltip
                title="Complete timeline of all assignment changes, status updates, and agent actions for this call request"
                placement="top"
              >
                <InfoCircleOutlined
                  style={{
                    color: "#1890ff",
                    cursor: "help",
                    fontSize: "16px",
                    marginLeft: "8px",
                  }}
                />
              </Tooltip>
            </Space>
          }
          size="small"
          style={{ marginBottom: 16 }}
        >
          {/* Current Assignment Summary */}
          {callRequest.assignedToUserId && (
            <div
              style={{
                padding: "12px",
                backgroundColor: "#f6ffed",
                border: "1px solid #52c41a",
                borderRadius: "6px",
                marginBottom: "16px",
              }}
            >
              <Space
                direction="vertical"
                size="small"
                style={{ width: "100%" }}
              >
                <Text strong style={{ color: "#52c41a" }}>
                  📋 Current Assignment Summary
                </Text>
                <Row gutter={16}>
                  <Col span={8}>
                    <Text>
                      <strong>Agent:</strong>{" "}
                      {getAgentName(callRequest.assignedToUserId)}
                    </Text>
                  </Col>
                  <Col span={8}>
                    <Text>
                      <strong>Status:</strong>{" "}
                      <Tag color={getStatusColor(callRequest.status)}>
                        {callRequest.status.toUpperCase()}
                      </Tag>
                    </Text>
                  </Col>
                  <Col span={8}>
                    <Text>
                      <strong>Assigned:</strong>{" "}
                      {formatDate(callRequest.updatedAt)}
                    </Text>
                  </Col>
                </Row>
              </Space>
            </div>
          )}

          {/* Event History */}
          <Space direction="vertical" style={{ width: "100%" }}>
            <Space>
              <Text strong>Event Timeline:</Text>
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={fetchEventsHistory}
                loading={loadingHistory}
              >
                Refresh
              </Button>
            </Space>
            {loadingHistory ? (
              <div style={{ textAlign: "center", padding: "20px" }}>
                <Spin />
                <div style={{ marginTop: "8px" }}>
                  <Text type="secondary">Loading event history...</Text>
                </div>
              </div>
            ) : eventsHistory.length === 0 ? (
              <div style={{ textAlign: "center", padding: "20px" }}>
                <Text type="secondary">No events recorded yet</Text>
              </div>
            ) : (
              <Timeline>
                {eventsHistory
                  .filter((event) => event && event.type)
                  .map((event) => {
                    const isAssignmentEvent = [
                      "call.assigned",
                      "call.reassigned",
                      "call.status_updated",
                    ].includes(event.type);

                    return (
                      <Timeline.Item
                        key={event._id}
                        color={getEventColor(event.type)}
                        dot={getEventIcon(event.type)}
                      >
                        <div>
                          <Space
                            direction="vertical"
                            size="small"
                            style={{ width: "100%" }}
                          >
                            <div>
                              <Text strong>
                                {getEventDescription(event.type)}
                              </Text>
                              {event.actorId && (
                                <Text
                                  type="secondary"
                                  style={{ marginLeft: "8px" }}
                                >
                                  by {getAgentName(event.actorId)}
                                </Text>
                              )}
                            </div>
                            <Text type="secondary" style={{ fontSize: "12px" }}>
                              {formatDate(event.timestamp)}
                            </Text>
                            {event.payload &&
                              Object.keys(event.payload).length > 0 && (
                                <div
                                  style={{
                                    marginTop: "8px",
                                    padding: "8px",
                                    background: "#f5f5f5",
                                    borderRadius: "4px",
                                    border: "1px solid #d9d9d9",
                                  }}
                                >
                                  <Text
                                    type="secondary"
                                    style={{
                                      fontSize: "11px",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    📋 Event Details:
                                  </Text>
                                  {Object.entries(event.payload).map(
                                    ([key, value]) => {
                                      // Handle special cases with user-friendly labels
                                      if (key === "assignedToUserId" && value) {
                                        return (
                                          <div
                                            key={key}
                                            style={{ marginTop: "4px" }}
                                          >
                                            <Text
                                              type="secondary"
                                              style={{ fontSize: "11px" }}
                                            >
                                              <strong>👤 Assigned To:</strong>{" "}
                                              {getAgentName(value)}
                                            </Text>
                                          </div>
                                        );
                                      }
                                      if (key === "actorId" && value) {
                                        return (
                                          <div
                                            key={key}
                                            style={{ marginTop: "4px" }}
                                          >
                                            <Text
                                              type="secondary"
                                              style={{ fontSize: "11px" }}
                                            >
                                              <strong>🎭 Action By:</strong>{" "}
                                              {getAgentName(value)}
                                            </Text>
                                          </div>
                                        );
                                      }
                                      if (key === "originalDate" && value) {
                                        return (
                                          <div
                                            key={key}
                                            style={{ marginTop: "4px" }}
                                          >
                                            <Text
                                              type="secondary"
                                              style={{ fontSize: "11px" }}
                                            >
                                              <strong>📅 Original Date:</strong>{" "}
                                              {formatDate(value)}
                                            </Text>
                                          </div>
                                        );
                                      }
                                      if (key === "originalTime" && value) {
                                        return (
                                          <div
                                            key={key}
                                            style={{ marginTop: "4px" }}
                                          >
                                            <Text
                                              type="secondary"
                                              style={{ fontSize: "11px" }}
                                            >
                                              <strong>⏰ Original Time:</strong>{" "}
                                              {value}
                                            </Text>
                                          </div>
                                        );
                                      }
                                      if (key === "bookingId" && value) {
                                        return (
                                          <div
                                            key={key}
                                            style={{ marginTop: "4px" }}
                                          >
                                            <Text
                                              type="secondary"
                                              style={{ fontSize: "11px" }}
                                            >
                                              <strong>🔖 Booking ID:</strong>{" "}
                                              <Text
                                                code
                                                style={{ fontSize: "10px" }}
                                              >
                                                {value}
                                              </Text>
                                            </Text>
                                          </div>
                                        );
                                      }
                                      if (key === "source" && value) {
                                        return (
                                          <div
                                            key={key}
                                            style={{ marginTop: "4px" }}
                                          >
                                            <Text
                                              type="secondary"
                                              style={{ fontSize: "11px" }}
                                            >
                                              <strong>📱 Source:</strong>{" "}
                                              <Tag size="small" color="blue">
                                                {value}
                                              </Tag>
                                            </Text>
                                          </div>
                                        );
                                      }
                                      if (key === "conversionType" && value) {
                                        return (
                                          <div
                                            key={key}
                                            style={{ marginTop: "4px" }}
                                          >
                                            <Text
                                              type="secondary"
                                              style={{ fontSize: "11px" }}
                                            >
                                              <strong>🔄 Conversion:</strong>{" "}
                                              <Tag size="small" color="blue">
                                                {value}
                                              </Tag>
                                            </Text>
                                          </div>
                                        );
                                      }
                                      // For other fields, show with user-friendly formatting
                                      return (
                                        <div
                                          key={key}
                                          style={{ marginTop: "4px" }}
                                        >
                                          <Text
                                            type="secondary"
                                            style={{ fontSize: "11px" }}
                                          >
                                            <strong>
                                              {key.charAt(0).toUpperCase() +
                                                key
                                                  .slice(1)
                                                  .replace(/([A-Z])/g, " $1")}
                                              :
                                            </strong>{" "}
                                            {typeof value === "string" &&
                                            value.includes("T")
                                              ? formatDate(value)
                                              : String(value)}
                                          </Text>
                                        </div>
                                      );
                                    }
                                  )}
                                </div>
                              )}
                          </Space>
                        </div>
                      </Timeline.Item>
                    );
                  })
                  .filter(Boolean)}
              </Timeline>
            )}
          </Space>
        </Card>

        {/* Notes and Status Updates */}
        <Card
          title={
            <Space>
              <EditOutlined />
              Notes & Status Updates
              <Tooltip
                title="Add notes, update status, and track all communications related to this call request"
                placement="top"
              >
                <InfoCircleOutlined
                  style={{
                    color: "#1890ff",
                    cursor: "help",
                    fontSize: "16px",
                    marginLeft: "8px",
                  }}
                />
              </Tooltip>
            </Space>
          }
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Space direction="vertical" style={{ width: "100%" }}>
            <div>
              <Text strong>Update Status:</Text>
              <Space style={{ marginLeft: "16px" }}>
                <Select
                  value={selectedStatus}
                  onChange={setSelectedStatus}
                  style={{ width: 150 }}
                  placeholder="Select status"
                >
                  <Option value="requested">Requested</Option>
                  <Option value="assigned">Assigned</Option>
                  <Option value="calling">Calling</Option>
                  <Option value="completed">Completed</Option>
                  <Option value="no_answer">No Answer</Option>
                  <Option value="reschedule">Reschedule</Option>
                </Select>
                <Input
                  placeholder="Add notes..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  style={{ width: 300 }}
                />
                <Button
                  type="primary"
                  onClick={handleStatusUpdate}
                  loading={loading}
                  disabled={!selectedStatus}
                >
                  Update Status
                </Button>
              </Space>
            </div>
            {callRequest.notes && (
              <div style={{ marginTop: "16px" }}>
                <Text strong>Current Notes:</Text>
                <div
                  style={{
                    padding: "12px",
                    backgroundColor: "#f5f5f5",
                    borderRadius: "6px",
                    marginTop: "8px",
                  }}
                >
                  <Text>{callRequest.notes}</Text>
                </div>
              </div>
            )}
          </Space>
        </Card>

        {/* Call Events History */}
        <Card
          title={
            <Space>
              <HistoryOutlined />
              Call Events History
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={fetchEventsHistory}
                loading={loadingHistory}
              >
                Refresh
              </Button>
            </Space>
          }
          size="small"
          style={{ marginBottom: 16 }}
        >
          {loadingHistory ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <Spin size="large" />
            </div>
          ) : eventsHistory.length > 0 ? (
            <Timeline>
              {eventsHistory.map((event, index) => (
                <Timeline.Item
                  key={event._id || index}
                  dot={getEventIcon(event.type)}
                  color={
                    event.type.includes("completed")
                      ? "green"
                      : event.type.includes("no_answer") ||
                          event.type.includes("escalated")
                        ? "red"
                        : event.type.includes("assigned")
                          ? "blue"
                          : "default"
                  }
                >
                  <div>
                    <Text strong>{getEventDescription(event.type)}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                      {formatDate(event.timestamp || event.createdAt)}
                    </Text>

                    {/* Show payload details if available */}
                    {event.payload && Object.keys(event.payload).length > 0 && (
                      <div style={{ marginTop: "8px" }}>
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          Details: {JSON.stringify(event.payload, null, 2)}
                        </Text>
                      </div>
                    )}
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          ) : (
            <div
              style={{ textAlign: "center", padding: "20px", color: "#999" }}
            >
              <HistoryOutlined
                style={{ fontSize: "24px", marginBottom: "8px" }}
              />
              <br />
              No events history available
            </div>
          )}
        </Card>

        {/* Actions */}
        <Card title="Quick Actions" size="small">
          <Space wrap>
            <Button
              type="primary"
              icon={<PhoneOutlined />}
              onClick={handleCallNow}
              disabled={!callRequest.phone}
            >
              Call Now
            </Button>
            <Button icon={<ReloadOutlined />} onClick={onUpdate}>
              Refresh
            </Button>
            <Button onClick={onClose}>Close</Button>
          </Space>
        </Card>
      </div>
    </Modal>
  );
};

export default CallRequestDetailModal;
